import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjectStore } from '../store/useProjectStore';
import { useAuthStore } from '../store/useAuthStore';
import { useSSE } from '../hooks/useSSE';
import { Header } from './layout/Header';
import { Sidebar } from './layout/Sidebar';
import { AgentTab } from './agent/AgentTab';
import { ResearchTab } from './research/ResearchTab';
import { ExtractionTab } from './extraction/ExtractionTab';
import { AnnotationTab } from './annotation/AnnotationTab';
import { ReviewTab } from './review/ReviewTab';
import { BoqTab } from './boq/BoqTab';
import { CablesTab } from './cables/CablesTab';
import { PromptsTab } from './prompts/PromptsTab';
import { AdminApp } from '../admin/AdminApp';
import { initApp } from '../init';
import {
  VALID_TABS, OWNER_ONLY_TABS, DEFAULT_TAB_OWNER, DEFAULT_TAB_ANNOTATOR, projectPath,
} from '../api/projectContext';
import type { TabId } from '../types';

/**
 * The project shell, rendered for `/p/:projectId/:tab`.
 *
 * Project identity and the active tab both come from the URL (no reloads,
 * no hash). Switching projects re-runs `initApp` and reconnects SSE scoped
 * to the new project; switching tabs is a pure client-side route change that
 * keeps every tab mounted (state preserved) and just toggles visibility.
 */
export function ProjectView() {
  const { projectId, tab } = useParams<{ projectId: string; tab?: string }>();
  const navigate = useNavigate();

  const sidebarCollapsed = useProjectStore((s) => s.sidebarCollapsed);
  const storeProject = useProjectStore((s) => s.project);
  const setActiveTab = useProjectStore((s) => s.setActiveTab);

  const authUser = useAuthStore((s) => s.user);
  const isOwner = authUser?.role === 'owner';
  const defaultTab: TabId = isOwner ? DEFAULT_TAB_OWNER : DEFAULT_TAB_ANNOTATOR;

  const [initializing, setInitializing] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // SSE stream, scoped to this project. Reconnects when projectId changes.
  useSSE(projectId ?? null);

  // Load (or reload) the project whenever the URL project id changes.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    // Clear the previous project's loaded data so stale data does not flash
    // while the new one loads. Stream state (agent feed, skill statuses) is
    // reset inside useSSE, before it drains buffered events.
    useProjectStore.getState().clearAllResults();
    useProjectStore.getState().setProject(null);
    setInitializing(true);
    setNotFound(false);

    // Retry init a few times before giving up: when a project is opened
    // straight from the dashboard, its S3 sync (which registers DB access)
    // may still be in flight, so the first /api/project can 403/404. Short
    // retries absorb that window; a genuinely missing project falls through
    // to "not found".
    const MAX_ATTEMPTS = 8;
    const RETRY_MS = 500;

    const attempt = async (n: number): Promise<void> => {
      try {
        await initApp();
      } catch (err) {
        console.error('initApp failed:', err);
      }
      if (cancelled) return;
      if (useProjectStore.getState().project) {
        setInitializing(false);
        return;
      }
      if (n + 1 >= MAX_ATTEMPTS) {
        setNotFound(true);
        setInitializing(false);
        return;
      }
      setTimeout(() => { if (!cancelled) attempt(n + 1); }, RETRY_MS);
    };
    attempt(0);

    return () => { cancelled = true; };
  }, [projectId]);

  // Resolve the effective tab and keep the URL canonical: fill in a missing
  // tab, and bounce annotators off owner-only tabs.
  const urlTab = tab && (VALID_TABS as string[]).includes(tab) ? (tab as TabId) : null;
  const blocked = urlTab != null && !isOwner && OWNER_ONLY_TABS.includes(urlTab);
  const activeTab: TabId = blocked ? defaultTab : (urlTab ?? defaultTab);

  useEffect(() => {
    if (!projectId) return;
    if (!urlTab || blocked) {
      navigate(projectPath(projectId, activeTab), { replace: true });
    }
  }, [projectId, urlTab, blocked, activeTab, navigate]);

  // Mirror the active tab into the store (Header highlight, attention clearing).
  useEffect(() => {
    setActiveTab(activeTab);
  }, [activeTab, setActiveTab]);

  const goTab = (t: TabId) => {
    if (projectId) navigate(projectPath(projectId, t));
  };
  const goHome = () => navigate('/');

  if (notFound) {
    return (
      <div style={{ color: '#E8EAF0', padding: 40 }}>
        <h2>Project not available</h2>
        <p style={{ opacity: 0.7, marginTop: 8 }}>
          It may not exist, or you don’t have access to it.
        </p>
        <button className="dashboard-start-btn" style={{ marginTop: 16 }} onClick={goHome}>
          ← Back to dashboard
        </button>
      </div>
    );
  }

  if (initializing || !storeProject) {
    return (
      <div className="project-loader">
        <div className="project-loader-ring">
          <span className="project-loader-mark">M</span>
        </div>
        <div className="project-loader-text">Loading project</div>
      </div>
    );
  }

  return (
    <>
      <Header
        onTabChange={goTab}
        onNewProject={isOwner ? goHome : undefined}
      />
      <div className="app-body">
        <Sidebar />
        <main className={`tab-panels ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          {isOwner && (
            <TabPanel id="pipeline" active={activeTab}>
              <AgentTab />
            </TabPanel>
          )}
          <TabPanel id="extraction" active={activeTab}>
            <ExtractionTab />
          </TabPanel>
          <TabPanel id="annotation" active={activeTab}>
            <AnnotationTab />
          </TabPanel>
          <TabPanel id="review" active={activeTab}>
            <ReviewTab />
          </TabPanel>
          <TabPanel id="boq" active={activeTab}>
            <BoqTab />
          </TabPanel>
          <TabPanel id="research" active={activeTab}>
            <ResearchTab />
          </TabPanel>
          {isOwner && (
            <TabPanel id="cables" active={activeTab}>
              <CablesTab />
            </TabPanel>
          )}
          {isOwner && (
            <TabPanel id="prompts" active={activeTab}>
              <PromptsTab />
            </TabPanel>
          )}
          {isOwner && (
            <TabPanel id="upload" active={activeTab}>
              <div className="w-full h-full overflow-y-auto">
                <AdminApp />
              </div>
            </TabPanel>
          )}
        </main>
      </div>
    </>
  );
}

function TabPanel({ id, active, children }: { id: TabId; active: TabId; children: React.ReactNode }) {
  const isActive = active === id;
  return (
    <section
      className={`tab-content${isActive ? ' active' : ''}`}
      style={isActive ? { display: 'flex' } : { display: 'none' }}
    >
      {children}
    </section>
  );
}
